import validator from 'validator';
/* utils */
import { getStringStrength } from '../../utils/info-helper';

const getFallbackParams = (params) => {
    if (typeof params.fallbackMin === 'number' || typeof params.fallbackMax === 'number') {
        const min = typeof params.fallbackMin === 'number' ? params.fallbackMin : params.min;
        const max = typeof params.fallbackMax === 'number' ? params.fallbackMax : params.max;
        const fallbackParams = { ...params };
        if (typeof params.fallbackMin === 'number' && (isNaN(params.min) || params.min < min || params.min > max)) {
            fallbackParams.min = params.fallbackMin;
        }
        if (typeof params.fallbackMax === 'number' && (isNaN(params.max) || params.max < min || params.max > max)) {
            fallbackParams.max = params.fallbackMax;
        }
        return fallbackParams;
    }
    return params;
};

const isLocalhost = (value, params) =>
    value.indexOf('localhost') > -1
    && validator.isURL(value, { require_tld: false, ...params });

const isOAuthRedirect = (value, params) =>
    value.substr(-15) === ':/oauthredirect'
    && validator.isURL(value.substr(0, value.length - 15), { require_tld: false, ...params });

export default {
    apiOldPasswordMismatch: {
        hint: () => 'Password is incorrect'
    },
    containsCharTypes: {
        rule: (value, params) => {
            const strength = getStringStrength(value);
            let validLength = 0;
            params.types.forEach((type) => {
                if (strength[type] > 0) {
                    validLength++;
                }
            });

            return validLength >= params.length;
        },
        hint: (value, params) => {
            const atLeast = [];
            const strength = getStringStrength(value);
            let testsPassed = params.types.length;

            if (params.types.indexOf('digits') >= 0 && !strength.digits) {
                atLeast.push('one digit');
                testsPassed--;
            }
            if (params.types.indexOf('special') >= 0 && !strength.special) {
                atLeast.push('one special character');
                testsPassed--;
            }
            if (params.types.indexOf('lowercase') >= 0 && !strength.lowercase) {
                atLeast.push('one lowercase letter');
                testsPassed--;
            }
            if (params.types.indexOf('uppercase') >= 0 && !strength.uppercase) {
                atLeast.push('one uppercase letter');
                testsPassed--;
            }

            if (atLeast.length === 1) {
                return `Must contain at least ${atLeast[0]}`;
            }
            return `Must contain at least ${params.length - testsPassed} of the following: ${atLeast.join(', ')}`;
        }
    },
    custom: {
        rule: (value, validation, components, name) => validation.rule(value, validation, components, name),
        hint: (value, validation) => validation.hint(value)
    },
    differsFrom: {
        rule: (value, params) => !value || value !== params.value,
        hint: (value, params) => {
            if (params.hint) {
                if (typeof params.hint === 'function') {
                    return params.hint(value, params);
                }
                return params.hint;
            }
            return `Shouldn't be same as ${params.label}`;
        }
    },
    email: {
        rule: value => !value || validator.isEmail(value),
        hint: value => `${value} is not a valid Email`
    },
    equalAs: {
        rule: (value, param, components) => {
            const field = components[param.prop] ? components[param.prop].state : null;

            return !field || value === field.value;
        },
        hint: (value, param) => `Should be equal as '${param.label}'`
    },
    maxLength: {
        rule: (value, length) => {
            if (!value) {
                return true;
            }
            if (Array.isArray(value)) {
                return value.length <= length;
            }
            return value.toString().trim().length <= length;
        },
        hint: (value, length) => `Maximum length exceeded (${value.length}/${length})`
    },
    minLength: {
        rule: (value, length) => {
            if (!value) {
                return true;
            }
            if (Array.isArray(value)) {
                return value.length >= length;
            }
            return value.toString().trim().length >= length;
        },
        hint: (value, length) => `Minimum length not met (${value.toString().trim().length}/${length})`
    },
    numeric: {
        rule: (value, params) => {
            if (typeof value !== 'number' && !value) {
                return true;
            }
            const fallbackParams = getFallbackParams(params);
            if (params.integer) {
                return validator.isInt((typeof value === 'number' ? value.toString() : value), fallbackParams);
            }
            return validator.isFloat((typeof value === 'number' ? value.toString() : value), fallbackParams);
        },
        hint: (value, params) => {
            const fallbackParams = getFallbackParams(params);
            let additionalMessage = '';
            if (fallbackParams.min !== undefined && fallbackParams.max !== undefined) {
                additionalMessage = ` between ${fallbackParams.min} and ${fallbackParams.max}`;
            } else if (fallbackParams.min !== undefined) {
                additionalMessage = ` higher than ${fallbackParams.min}`;
            } else if (fallbackParams.max !== undefined) {
                additionalMessage = ` lower than ${fallbackParams.max}`;
            }
            let isInt = '';
            if (fallbackParams.integer) {
                isInt = 'whole ';
            }
            return `Must be a ${isInt}number${additionalMessage}`;
        }
    },
    required: {
        rule: (value, flag) => {
            if (!flag) {
                return true;
            }
            if (typeof value === 'undefined') {
                return false;
            }
            if (typeof value === 'object') {
                if (Array.isArray(value)) {
                    return value.length > 0;
                }
                return !!value;
            }
            return !!value.toString().trim();
        },
        hint: () => 'Required'
    },
    similarity: {
        rule: (value, params) => {
            const val = value.toLowerCase();
            const test = params.testValue.toLowerCase();
            const length = params.length;
            let similar = false;
            for (let i = 0; i <= val.length - length; i++) {
                if (test.indexOf(val.substring(i, i + length)) >= 0) {
                    similar = true;
                    break;
                }
            }
            return !similar;
        },
        hint: (value, params) => `Too similar to ${params.testLabel}`
    },
    strength: {
        rule: (value, conditions) => {
            const strength = getStringStrength(value);
            let valid = true;
            Object.keys(conditions).forEach((key) => {
                if (strength[key] < conditions[key]) {
                    valid = false;
                }
            });

            return valid;
        },
        hint: (value, conditions) => {
            const hints = [];
            const atLeast = [];
            const strength = getStringStrength(value);
            const alphanumeric = strength.lowercase + strength.uppercase + strength.digits;

            if (conditions.totalScore && strength.totalScore < conditions.totalScore) {
                hints.push('Too weak');
            }
            if (conditions.alphanumeric && alphanumeric < conditions.alphanumeric) {
                atLeast.push(
                    `${conditions.alphanumeric} alphanumeric character${conditions.alphanumeric > 1 ? 's' : ''}`
                );
            }
            if (conditions.diffChars && strength.diffChars < conditions.diffChars) {
                atLeast.push(`${conditions.diffChars} different characters`);
            }
            if (conditions.digits && strength.digits < conditions.digits) {
                atLeast.push(`${conditions.digits} digit${conditions.digits > 1 ? 's' : ''}`);
            }
            if (conditions.special && strength.special < conditions.special) {
                atLeast.push(`${conditions.special} special character${conditions.special > 1 ? 's' : ''}`);
            }
            if (conditions.lowercase && strength.lowercase < conditions.lowercase) {
                atLeast.push(`${conditions.lowercase} lowercase letter${conditions.lowercase > 1 ? 's' : ''}`);
            }
            if (conditions.uppercase && strength.uppercase < conditions.uppercase) {
                atLeast.push(`${conditions.uppercase} uppercase letter${conditions.uppercase > 1 ? 's' : ''}`);
            }
            if (atLeast.length > 0) {
                hints.push(`Must contain at least: ${atLeast.join(', ')}`);
            }

            return hints.join('; ');
        }
    },
    url: {
        rule: (value, params) => {
            const validatorParams = typeof params === 'boolean' ? undefined : params;
            const trimmedValue = value.trim();
            return !trimmedValue
                || validator.isURL(trimmedValue, validatorParams)
                || isLocalhost(trimmedValue, validatorParams)
                || isOAuthRedirect(trimmedValue, validatorParams);
        },
        hint: value => `${value} is not a valid URL`
    }
};
